import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  type?: 'website' | 'article' | 'book';
  url?: string;
  jsonLd?: object;
  noindex?: boolean;
}

const SITE_NAME = 'Aude Rights Agency';
const DEFAULT_IMAGE = 'https://hgkmyajrzjjxtaijugho.supabase.co/storage/v1/object/public/aboutus/aude-portrait.png';
const SITE_URL = 'https://www.aude-rights.com';

const SEO = ({ 
  title, 
  description, 
  image = DEFAULT_IMAGE, 
  type = 'website',
  url,
  jsonLd,
  noindex = false
}: SEOProps) => {
  const fullTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
  const truncatedDescription = description.length > 155 
    ? description.slice(0, 155).trim() + '...' 
    : description;
  const fullUrl = url || SITE_URL;

  // Default Organization schema for the site
  const defaultOrganizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Aude Rights Agency",
    "url": SITE_URL,
    "logo": `${SITE_URL}/assets/aude-logo-full-Ceg8IdAj.png`,
    "description": "Literary rights agency representing publishers of children's books and adult non-fiction from Germany, France, Greece, Poland and Italy.",
    "sameAs": [
      "https://www.instagram.com/auderights/",
      "https://www.linkedin.com/in/aude-lavielle-72246451/"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "contact@aude-rights.com",
      "contactType": "customer service"
    }
  };

  const schemaToUse = jsonLd || defaultOrganizationSchema;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={truncatedDescription} />
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={truncatedDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_GB" />
      {image && <meta property="og:image" content={image} />}
      {fullUrl && <meta property="og:url" content={fullUrl} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={truncatedDescription} />
      {image && <meta name="twitter:image" content={image} />}
      
      {/* Canonical */}
      {fullUrl && <link rel="canonical" href={fullUrl} />}

      {/* JSON-LD Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify(schemaToUse)}
      </script>
    </Helmet>
  );
};

export default SEO;
